import React, { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useReactiveVar } from '@apollo/client'
import { useFormContext } from 'react-hook-form'

import { artistVar, currentUserVar, showMyFilterForZoneVar, gpsUnitsVar, labelCodeIdVar } from 'modulesCommon/store'

import { useCreateArtist, useCreateProducts, useCreateProject } from 'modules/ProjectCreate/graphql/mutations'

import { ConfirmationForm } from 'modules/ProjectCreate/components'
import { IProductFormArray } from '../types'

import { IArtist, IProduct } from 'shared/interfaces'
import { useLoading, useServerError } from 'shared/hooks'
import { updateISAACLink } from 'shared/utils/updateISAACLink'
import { useZonesList } from 'modulesCommon/serviceHooks'

const ConfirmationStep = () => {
  const navigate = useNavigate()
  const { getValues } = useFormContext()

  const gpsUnits = useReactiveVar(gpsUnitsVar)
  const currentUser = useReactiveVar(currentUserVar)
  const zoneId = useReactiveVar(labelCodeIdVar)
  const { getUnitsByZoneId } = useZonesList()

  const { mutate: createArtist, loading: createArtistLoading, error: createArtistError } = useCreateArtist()
  const { mutate: createProduct, loading: createProductLoading, error: createProductError } = useCreateProducts()
  const { mutate: createProject, loading: createProjectLoading, error: createProjectError } = useCreateProject()

  const artist = useMemo(() => {
    const [id, name, label_id, gps_status, opus_id, facebook_id, spotify_id] = getValues([
      'artistId',
      'artistName',
      'firstCreatedIn',
      'gpsStatus',
      'opusId',
      'facebookId',
      'spotifyId'
    ])
    const gpsStatusLabel = gpsUnits.find((gpsUnit: any) => gpsUnit.value === gps_status)?.label ?? 'None'

    return { id, name, label_id, gps_status, opus_id, facebook_id, spotify_id, gpsStatusLabel }
  }, [getValues, gpsUnits])

  const project = useMemo(() => {
    const [name, unit_id, isaac_url, info, team_info] = getValues([
      'projectName',
      'unit',
      'isaacUrl',
      'info',
      'teamInfo'
    ])
    const unitLabel = getUnitsByZoneId(zoneId).find((unit) => unit.id === unit_id)?.name ?? ''

    return { name, label_id: zoneId, unit_id, isaac_url, info, team_info, unitLabel }
  }, [getValues, zoneId, getUnitsByZoneId])

  const projectProducts = useMemo(() => getValues('products'), [getValues])

  const onCreateArtist = useCallback(async () => {
    // opus_id = id if artist is in gcdm only, so we dont have it in our DB and should create a new one
    if (artist.id && artist.id !== artist.opus_id) return artist

    const { name, label_id, gps_status, opus_id } = artist
    try {
      const { data } = await createArtist({
        variables: {
          input: { name, label_id: label_id || currentUser?.label_id, gps_status, opus_id }
        }
      })
      artistVar(data?.createArtist)
      return data?.createArtist
    } catch {
      return null
    }
  }, [createArtist, artist, currentUser?.label_id])

  const onCreateProducts = useCallback(
    async (artistId: string) => {
      const sortedProducts = projectProducts?.reduce(
        (result: { created: IProductFormArray[]; existing: IProductFormArray[] }, product: IProductFormArray) => ({
          ...result,
          ...(!product.id && { created: [...result.created, product] }),
          ...(product.id && { existing: [...result.existing, product] })
        }),
        { created: [], existing: [] }
      )
      if (sortedProducts?.created.length) {
        try {
          const { data } = await createProduct({
            variables: {
              input: sortedProducts.created.map((product: any) => ({
                artist_id: artistId,
                name: product.name,
                configuration: product.configuration,
                gps_status: product.gps_status,
                ...(product.gpid_list && { gpid_list: product.gpid_list })
              }))
            }
          })

          return projectProducts.map((existingProduct: IProductFormArray) => {
            const created = data?.createProducts?.find(
              (createdProduct: IProduct) =>
                createdProduct.name === existingProduct.name &&
                createdProduct.configuration === existingProduct.configuration
            )
            return { ...created, ...existingProduct }
          })
        } catch {
          return sortedProducts?.existing
        }
      }

      return projectProducts || []
    },
    [createProduct, projectProducts]
  )

  const onCreateProject = useCallback(
    async (existingArtist: IArtist, products: IProduct[]) => {
      const { label_id, unit_id, isaac_url, name, info, team_info } = project

      try {
        const { data } = await createProject({
          variables: {
            input: {
              name,
              info,
              team_info,
              artist_id: existingArtist.id,
              label_id: label_id,
              unit_id: unit_id,
              isaac_url: updateISAACLink(isaac_url),
              products: products.map((product) => ({
                product_id: product.id
                  ? String(product.id)
                  : getValues('artistProducts').find(
                    (item: IProduct) => item.name === product.name && item.configuration === product.configuration
                  )?.id,
                publish_date: product.publish_date,
                radio_updates: product.radio_updates,
                dsp_updates: product.dsp_updates,
                active: true,
                main: false
              }))
            }
          }
        })
        return data?.createCampaign
      } catch {
        return null
      }
    },
    [getValues, createProject, project]
  )

  const goToNextStep = useCallback(async () => {
    const savedArtist = await onCreateArtist()
    if (savedArtist.id) {
      const savedProducts = await onCreateProducts(savedArtist.id)

      const campaign = await onCreateProject(savedArtist, savedProducts)
      if (campaign.id) {
        showMyFilterForZoneVar(true)
        navigate('/projects', { state: { isNew: true, artistName: artist.name, projectName: project.name }})
      }
    }
  }, [onCreateArtist, onCreateProducts, onCreateProject, navigate, artist.name, project.name])

  useLoading({ isLoading: createArtistLoading || createProjectLoading || createProductLoading })
  useServerError([createArtistError, createProjectError, createProductError])

  return <ConfirmationForm onGoToNextStep={goToNextStep} artist={artist} project={project} products={projectProducts} />
}

export default ConfirmationStep
